import cx from "classnames";
import type { FC, RefAttributes } from "react";
import { forwardRef, useEffect, useRef, useState } from "react";
import TextareaAutosize, { type TextareaAutosizeProps } from "react-textarea-autosize";

import styles from "../InputText/_.module.css";

import type { InputTextProps } from "../InputText";

export type InputTextBoxProps = Omit<InputTextProps, "segmentPosition" | "ref"> &
	TextareaAutosizeProps &
	RefAttributes<HTMLTextAreaElement> & {
		minRows?: number;
		maxRows?: number;
		disabled?: boolean;
		placeholder?: string;
	};

export const InputTextBox: FC<InputTextBoxProps> = forwardRef(
	(
		{
			className = "",
			placeholder = "Value",
			value,
			disabled: isDisabled = false,
			variant = "default",
			size = "lg",
			disableElevation = false,
			fullWidth = false,
			minRows,
			maxRows,
			...props
		},
		ref
	) => {
		const internalRef = useRef<HTMLTextAreaElement | null>(null);
		const hiddenRef = useRef<HTMLTextAreaElement | null>(null);
		const classNames = [
			styles["input-text"],
			styles["input-text--segment-none"],
			Array.isArray(variant)
				? variant.map((i) => "input-text--" + i).join(" ")
				: "input-text--" + variant,
			size ? styles[`input-text--${size}`] : "",
			isDisabled ? styles["input-text--disabled"] : "",
			fullWidth ? "w-full" : "",
		];
		const [rows, setRows] = useState(minRows || 1);

		const handleContainerClick = () => {
			internalRef.current?.focus();
		};

		useEffect(() => {
			const hiddenEl = hiddenRef.current;

			if (!hiddenEl) {
				return;
			}

			const scrollHeight = hiddenEl.scrollHeight;
			const paddingBottom = parseInt(getComputedStyle(hiddenEl).paddingBottom, 10);
			const paddingTop = parseInt(getComputedStyle(hiddenEl).paddingTop, 10);
			const lineHeight = parseInt(getComputedStyle(hiddenEl).lineHeight, 10);
			const rows = (scrollHeight - paddingBottom - paddingTop) / lineHeight;

			if (minRows && rows < minRows) {
				setRows(minRows);
			} else if (maxRows && rows > maxRows) {
				setRows(maxRows);
			} else {
				setRows(rows);
			}
		}, [maxRows, minRows, value]);

		return (
			<div
				className={cx(classNames, className)}
				data-disableElevation={disableElevation}
				onClick={handleContainerClick}
				tabIndex={-1}
				role="textbox"
				onKeyUp={() => undefined}
				aria-label="textbox-container"
			>
				{/* Visible Text Area */}
				<TextareaAutosize
					{...props}
					ref={(node) => {
						internalRef.current = node;
						if (!ref) {
							return;
						}
						if (typeof ref === "function") {
							ref(node);
						} else {
							ref.current = node;
						}
					}}
					className="resize-none"
					aria-label="textbox"
					placeholder={placeholder}
					value={value}
					disabled={isDisabled}
					rows={rows}
				/>

				{/* Hidden Text Area for calculation purposes */}
				<textarea
					{...props}
					ref={hiddenRef}
					className="pointer-events-none absolute -z-50 resize-none overflow-hidden opacity-0"
					aria-hidden="true"
					tabIndex={-1}
					value={value}
					readOnly
					rows={1}
					style={{ width: internalRef.current?.clientWidth }}
				/>
			</div>
		);
	}
);

InputTextBox.displayName = "InputTextBox";
